/**
 * Token Usage Chart Component
 *
 * Stacked area chart showing LLM token usage (input and output) over time.
 * Essential for cost monitoring and usage optimization.
 */

import { useId, useMemo, useCallback } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';
import { formatChartTime, formatCompactNumber } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import {
  createDotProps,
  createActiveDotProps,
  createGradientIds,
} from './chart-utils';
import { MetricTooltip } from './MetricTooltip';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { DataPoint, TokenSummary } from '@/types/dashboard';

interface TokenUsageChartProps {
  inputData: DataPoint[] | undefined;
  outputData: DataPoint[] | undefined;
  summary: TokenSummary | undefined;
  isLoading: boolean;
}

interface MergedTokenPoint {
  time: string;
  timestamp: string; // Original ISO timestamp for sorting
  input: number;
  output: number;
}

function mergeTokenData(
  inputData: DataPoint[] | undefined,
  outputData: DataPoint[] | undefined,
): MergedTokenPoint[] {
  const dataMap = new Map<string, MergedTokenPoint>();

  // Add input token data
  // Use original timestamp as key to properly merge data points
  inputData?.forEach((point) => {
    dataMap.set(point.timestamp, {
      time: formatChartTime(point.timestamp),
      timestamp: point.timestamp,
      input: Math.round(point.value),
      output: 0,
    });
  });

  // Merge output token data
  outputData?.forEach((point) => {
    const existing = dataMap.get(point.timestamp);
    if (existing) {
      existing.output = Math.round(point.value);
    } else {
      dataMap.set(point.timestamp, {
        time: formatChartTime(point.timestamp),
        timestamp: point.timestamp,
        input: 0,
        output: Math.round(point.value),
      });
    }
  });

  // Sort chronologically by original timestamp
  return Array.from(dataMap.values()).sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  );
}

const chartConfig = {
  input: {
    label: 'Input Tokens',
    color: 'var(--chart-1)',
  },
  output: {
    label: 'Output Tokens',
    color: 'var(--chart-2)',
  },
} satisfies ChartConfig;

export function TokenUsageChart({
  inputData,
  outputData,
  summary,
  isLoading,
}: TokenUsageChartProps) {
  // Generate unique gradient IDs for this chart instance
  const chartId = useId();
  const gradientIds = useMemo(
    () => createGradientIds(chartId, ['fillInput', 'fillOutput']),
    [chartId],
  );

  // Memoize data transformation to avoid recalculating on every render
  const chartData = useMemo(
    () => mergeTokenData(inputData, outputData),
    [inputData, outputData],
  );

  // Memoize legend reordering callback (output on top, so list it first)
  const renderLegend = useCallback(
    (props: { payload?: readonly LegendPayload[] }) => {
      const reorderedPayload = props.payload
        ? [...props.payload].sort((a, b) => {
            const order = ['output', 'input'];
            return (
              order.indexOf(String(a.dataKey)) -
              order.indexOf(String(b.dataKey))
            );
          })
        : [];
      return <ChartLegendContent {...props} payload={reorderedPayload} />;
    },
    [],
  );

  // Check if there's any data with values
  const hasData = chartData.some((d) => d.input > 0 || d.output > 0);

  // Show skeleton when loading OR when data hasn't arrived yet (props are undefined)
  const showSkeleton =
    isLoading || (inputData === undefined && outputData === undefined);

  // Show "no data" only when data has loaded but is genuinely empty
  const isEmpty = !showSkeleton && (chartData.length === 0 || !hasData);

  // Header summary content with tooltips for metric explanations
  const headerContent = summary && (
    <div className="flex gap-4 text-sm text-muted-foreground">
      <MetricTooltip
        label="Total"
        value={formatCompactNumber(summary.totalTokens)}
        tooltip={METRIC_DESCRIPTIONS.totalTokens}
      />
      <MetricTooltip
        label="Avg/Query"
        value={formatCompactNumber(summary.avgTokensPerQuery)}
        tooltip={METRIC_DESCRIPTIONS.avgTokensPerQuery}
      />
    </div>
  );

  return (
    <ChartCard
      title="Token Usage"
      tooltip={METRIC_DESCRIPTIONS.tokenUsage}
      headerContent={headerContent}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyMessage="No token usage data available for the selected time range"
      emptyAriaLabel="No token usage data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <AreaChart data={chartData} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => formatCompactNumber(v)}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={renderLegend} />
          <defs>
            <linearGradient
              id={gradientIds.fillInput}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-input)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-input)"
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient
              id={gradientIds.fillOutput}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-output)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-output)"
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="input"
            name="Input Tokens"
            type="monotone"
            stackId="1"
            fill={`url(#${gradientIds.fillInput})`}
            fillOpacity={0.4}
            stroke="var(--color-input)"
            strokeWidth={2}
            dot={createDotProps(chartData.length, 'var(--color-input)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-input)',
            )}
          />
          <Area
            dataKey="output"
            name="Output Tokens"
            type="monotone"
            stackId="1"
            fill={`url(#${gradientIds.fillOutput})`}
            fillOpacity={0.4}
            stroke="var(--color-output)"
            strokeWidth={2}
            dot={createDotProps(chartData.length, 'var(--color-output)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-output)',
            )}
          />
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}
